/**
 * Bemanning gjennom dagen, chart v3.1 (#273, docs/design/prototypes/oversikt-v31-handoff/DESIGN.md «Daily
 * staffing chart»): blue steps are the people planned at once, published shifts only, with planned lunch and
 * approved absence taken out (cover.ts staffWork); a continuous dark stepped line is the need; amber hatching
 * fills only the unmet part between the two. The same work feeds the gaps listed under it (hourGaps), so the
 * chart, the gaps and «Dette trenger deg» count alike. Time runs in real instants, so a DST day has 23 or 25
 * hour slots. Each gap is also a labelled button under the chart (DagenTab), the keyboard's way in; the hit
 * areas here are the mouse's and are hidden from assistive technology.
 */
import { T } from '@/i18n';
import { oslo, osloISO } from '@/rules';
import { type NeedProfile, needAt } from '@/shell/local';
import { addDays, dow } from '@timefly/core';
import { useEffect, useRef, useState } from 'react';
import { clock, span } from '../dagen/today';
import { type HourGap, gapText, pieces, type workOf } from '../vaktplan/staffing';

const HOUR = 3_600_000;
const hh = (h: number) => String(h).padStart(2, '0');

/** A step for the people axis that gives three or four lines whatever the day's demand (#273 T03). */
export function peopleStep(top: number): number {
  return [1, 2, 5, 10, 20, 50, 100].find((s) => top / s <= 4) ?? 200;
}

/** A gap's instants on the day; an end at 24:00 is the next midnight. */
export function gapSpan(d: string, g: HourGap): [number, number] {
  const a = Date.parse(osloISO(d, g.from));
  const b = g.to === '24:00' ? Date.parse(osloISO(addDays(d, 1), '00:00')) : Date.parse(osloISO(d, g.to));
  return [a, b];
}

/**
 * The chart's model, apart from drawing it: the hours shown, the planned steps, the need per hour and the
 * unmet parts (#273 OV3-04). The shown day is the opening hours, every shift starting that day and every
 * hour with a need, whichever reaches further: never cropped to the shortage.
 */
export function coverModel(
  d: string,
  need: NeedProfile,
  work: ReturnType<typeof workOf>,
  hoursFrom: [number, number],
) {
  const needHrs = Object.entries(need?.[dow(d)] ?? {})
    .filter(([, n]) => Number(n) > 0)
    .map(([h]) => Number(h));
  const from = Math.min(hoursFrom[0], ...needHrs);
  const to = Math.max(hoursFrom[1], ...needHrs.map((h) => h + 1));
  const at = (h: number) =>
    h >= 24
      ? Date.parse(osloISO(addDays(d, 1), '00:00')) + (h - 24) * HOUR
      : Date.parse(osloISO(d, `${hh(h)}:00`));
  const t0 = at(from);
  const t1 = at(to);
  const needOf = (t: number) => {
    const o = oslo(new Date(t).toISOString());
    return o && o.date === d ? needAt(need, dow(d), Number(o.time.slice(0, 2))) : 0;
  };
  const slots: { a: number; b: number; need: number; label: string }[] = [];
  for (let t = t0; t < t1; t += HOUR)
    slots.push({
      a: t,
      b: t + HOUR,
      need: needOf(t + 1),
      label: oslo(new Date(t).toISOString())?.time.slice(0, 2) ?? '',
    });
  // cut at every hour as well as every shift edge and lunch, as hourGaps does: the need changes on the hour
  const steps = slots.flatMap((sl) => pieces(work, sl.a, sl.b).map((p) => ({ ...p, need: sl.need })));
  const hasNeed = slots.some((s) => s.need > 0);
  const unmet = steps.filter((s) => s.have < s.need);
  const top = Math.max(1, ...steps.map((s) => Math.max(s.have, s.need)));
  return { t0, t1, steps, slots, hasNeed, unmet, top };
}

export function Coverage({
  d,
  need,
  work,
  gaps,
  shifts,
  openingHours,
  current,
  now,
  onPick,
  absenceKnown,
}: {
  absenceKnown: boolean;
  d: string;
  need: NeedProfile;
  work: ReturnType<typeof workOf>;
  gaps: HourGap[];
  shifts: Parameters<typeof span>[0];
  openingHours: Parameters<typeof span>[2];
  current: boolean;
  /** the present, on today only */
  now: number | null;
  onPick: (i: number) => void;
}) {
  const m = coverModel(d, need, work, span(shifts, d, openingHours));
  const tick = peopleStep(m.top);
  const maxY = Math.ceil(m.top / tick) * tick;
  const ticks = Array.from({ length: maxY / tick + 1 }, (_, i) => i * tick);
  const step = 36;
  const x0 = 44;
  const W = x0 + m.slots.length * step + 12;
  const X = (t: number) => x0 + ((t - m.t0) / HOUR) * step;
  // the chart scales with its card: text and height are set in its own units so they come out the same on
  // screen, 13 px labels and a plot at least 140 px high (#272 A-17, A-S09)
  const svg = useRef<SVGSVGElement>(null);
  const [fs, setFs] = useState(13);
  const [every, setEvery] = useState(2);
  const [tall, setTall] = useState(1);
  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const fit = () => {
      const w = el.getBoundingClientRect().width;
      if (!w) return;
      setFs((13 * W) / w);
      // a label every 2, 3, 4 or 6 hours, the first that leaves 44 px between two
      const px = (step * w) / W;
      setEvery([2, 3, 4, 6].find((n) => n * px >= 44) ?? 6);
      setTall(Math.max(1, ((140 * W) / w - 16 - 2.8 * ((13 * W) / w)) / 100));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [W]);
  const plotTop = 10 + fs * 1.4;
  const base = plotTop + 100 * tall;
  const sc = (100 * tall) / maxY;
  const Y = (n: number) => base - n * sc;
  const labY = base + 6 + fs * 0.6;
  // the need as one continuous stepped line across the shown hours, zero included
  const needPath = m.slots.map((s, i) => `${i === 0 ? 'M' : 'L'}${X(s.a)},${Y(s.need)} H${X(s.b)}`).join(' ');
  const nowX = now != null && now >= m.t0 && now < m.t1 ? X(now) : null;
  const summary = [
    T('Planlagte personer mot behovet, {from} til {to}', {
      from: m.slots[0]?.label ?? '',
      to: oslo(new Date(m.t1).toISOString())?.time.slice(0, 2) ?? '',
    }),
    gaps.length
      ? `${T('Under behov')}: ${gaps.map((g) => `${gapText(g)} ${T('{n} av {of}', { n: g.have, of: g.need })}`).join(', ')}`
      : m.hasNeed
        ? T('Planen dekker behovet hele dagen.')
        : T('Behov (ikke satt)'),
  ].join('. ');
  return (
    <div className="oxcover" data-testid="ov-cover">
      {!current && <p className="oxmute">{T('Sist hentede tall, ikke oppdatert')}</p>}
      <div className="dchart">
        <svg
          ref={svg}
          className="svg"
          viewBox={`0 0 ${W} ${labY + fs * 0.8}`}
          role="img"
          aria-label={summary}
          data-testid="coverage"
        >
          <defs>
            <pattern
              id="ox-hatch"
              width="6"
              height="6"
              patternUnits="userSpaceOnUse"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" style={{ fill: 'var(--warn-f)' }} />
              <line x1="0" y1="0" x2="0" y2="6" strokeWidth={2} style={{ stroke: 'var(--warn-l)' }} />
            </pattern>
          </defs>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={40} y1={Y(v)} x2={W - 8} y2={Y(v)} style={{ stroke: 'var(--hair)' }} />
              <text
                x={32}
                y={Y(v)}
                dominantBaseline="middle"
                textAnchor="end"
                fontSize={fs}
                fontWeight={500}
                style={{ fill: 'var(--ink2)' }}
                data-testid="cover-tick"
              >
                {v}
              </text>
            </g>
          ))}
          {m.steps.map(
            (s) =>
              s.have > 0 && (
                <rect
                  key={`p${s.a}`}
                  x={X(s.a)}
                  y={Y(s.have)}
                  width={Math.max(0, X(s.b) - X(s.a) - 1)}
                  height={s.have * sc}
                  style={{ fill: 'var(--chart)' }}
                  data-testid="cover-bar"
                >
                  <title>
                    {`${clock(new Date(s.a).toISOString())}–${clock(new Date(s.b).toISOString())}: ${
                      m.hasNeed
                        ? T('{n} av {of} planlagt', { n: s.have, of: s.need })
                        : T('{n} planlagt', { n: s.have })
                    }`}
                  </title>
                </rect>
              ),
          )}
          {m.unmet.map((s) => (
            <rect
              key={`u${s.a}`}
              x={X(s.a)}
              y={Y(s.need)}
              width={Math.max(0, X(s.b) - X(s.a) - 1)}
              height={(s.need - s.have) * sc}
              fill="url(#ox-hatch)"
              data-testid="cover-unmet"
            />
          ))}
          {m.hasNeed && (
            <path
              d={needPath}
              fill="none"
              strokeWidth={2}
              strokeLinejoin="miter"
              style={{ stroke: 'var(--ink1)' }}
              data-testid="cover-need"
            />
          )}
          {m.slots.map(
            (s, i) =>
              i % every === 0 && (
                <text
                  key={s.a}
                  x={X(s.a)}
                  y={labY}
                  dominantBaseline="middle"
                  textAnchor="middle"
                  fontSize={fs}
                  fontWeight={500}
                  style={{ fill: 'var(--ink2)' }}
                >
                  {s.label}
                </text>
              ),
          )}
          {nowX != null && now != null && (
            <g data-testid="cover-now">
              <line
                x1={nowX}
                y1={plotTop - 2}
                x2={nowX}
                y2={base}
                strokeWidth={1.5}
                strokeDasharray="3 3"
                style={{ stroke: 'var(--ink1)' }}
              />
              <text
                x={nowX + 4}
                y={plotTop - fs * 0.5}
                dominantBaseline="middle"
                fontSize={fs}
                fontWeight={600}
                style={{ fill: 'var(--ink1)' }}
              >
                {`${T('Nå')} ${clock(new Date(now).toISOString())}`}
              </text>
            </g>
          )}
          {/* the mouse's way into a gap; the buttons under the chart are the keyboard's and the reader's; role=img keeps these out of the reading */}
          <g>
            {gaps.map((g, i) => {
              const [a, b] = gapSpan(d, g);
              const x = X(Math.max(a, m.t0));
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: each gap has a labelled button under the chart
                <rect
                  key={`${g.from}-${g.to}`}
                  x={x}
                  y={plotTop - 4}
                  width={Math.max(0, X(Math.min(b, m.t1)) - x)}
                  height={base - plotTop + 4}
                  className="oxhit"
                  data-testid="cover-band"
                  onClick={() => onPick(i)}
                />
              );
            })}
          </g>
        </svg>
      </div>
      <div className="legend2">
        <span>
          <i style={{ background: 'var(--chart)' }} />
          {T('Planlagt')}
        </span>
        <span className={m.hasNeed ? '' : 'mute'}>
          <i className="line" />
          {m.hasNeed ? T('Behov') : T('Behov (ikke satt)')}
        </span>
        {m.unmet.length > 0 && (
          <span>
            <i className="band" />
            {T('Mangler')}
          </span>
        )}
      </div>
      <p className="oxmute oxbasis">
        {absenceKnown
          ? T('Publiserte vakter. Godkjent fravær og planlagt lunsj er trukket fra.')
          : T(
              'Publiserte vakter, planlagt lunsj trukket fra. Fravær er ikke trukket fra, det kunne ikke leses.',
            )}
      </p>
    </div>
  );
}
